import { useCurrentUser } from '@cma/app'
import { usePaginate } from '@cma/common'
import {
  PlatformClientContentPartsFragment,
  PlatformClientFolderPartsFragment
} from '@cma/generated/graphql'
import { useState } from 'react'
import { useDebounce } from 'usehooks-ts'
import { usePlatformContent } from './usePlatformContent'
import { filterPlatformContent } from './utils'

export enum FileSortEnum {
  Recent = 'recent',
  All = 'all',
  Shared = 'shared',
  Folders = 'folders'
}

export function useFileQuery() {
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<FileSortEnum>(FileSortEnum.All)
  const [selectedContents, setSelectedContent] = useState<string[]>([])
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const debouncedSearch = useDebounce(search, 500)
  const paginate = usePaginate({ limit: 10 })

  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformContent } = {} } = usePlatformContent({
    id: currentUser?.account?.id || ''
  })

  const navigateToFolder = (folderId: string | null) => {
    setCurrentFolderId(folderId)
    setSelectedContent([])

    if (folderId && sortKey === FileSortEnum.Folders) {
      setSortKey(FileSortEnum.All)
    }
  }

  const selectAllFiles = () => {
    setSelectedContent(filteredContents.map((file) => file.id))
  }

  const deselectAllFiles = () => {
    setSelectedContent([])
  }

  const toggleFileSelection = (fileId: string) => {
    setSelectedContent((prev) => {
      if (prev.includes(fileId)) {
        return prev.filter((id) => id !== fileId)
      } else {
        return [...prev, fileId]
      }
    })
  }

  const files = (platformContent?.customContents ?? []).map((file) => ({
    ...file,
    displayName: file.displayName || 'Untitled'
  })) as PlatformClientContentPartsFragment[]
  ;[]

  const folders = (platformContent?.folders ?? []).map((folder) => ({
    ...folder,
    displayName: folder.displayName || 'Untitled'
  })) as PlatformClientFolderPartsFragment[]

  const contents = [...folders, ...files]

  const allContents = contents.reduce<
    (PlatformClientContentPartsFragment | PlatformClientFolderPartsFragment)[]
  >((contents, content) => {
    if (!currentFolderId) {
      contents.push(content)
      return contents
    }

    if (content.__typename === 'Folder' && content.id === currentFolderId) {
      contents.push(...(content.customPages ?? []))
    }

    return contents
  }, [])

  const filteredContents = filterPlatformContent(
    allContents.filter((file) => {
      return (
        !debouncedSearch ||
        file?.displayName?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    }),
    sortKey
  )

  const currentFolder =
    folders?.find((folder) => folder.id === currentFolderId) || null

  const isAllSelected =
    !!filteredContents.length &&
    selectedContents.length === filteredContents.length

  return {
    contents,
    filteredContents,
    search,
    debouncedSearch,
    sortKey,
    paginate,
    selectedContents,
    isAllSelected,
    currentFolderId,
    currentFolder,
    setSearch,
    setSortKey,
    selectAllFiles,
    deselectAllFiles,
    toggleFileSelection,
    navigateToFolder
  }
}
